import React, { useState, useEffect } from "react";
import { analyzeUrlStrategies } from "../api";
import { getScoreColor } from "../components/ui/ScoreRing";
import { 
  FlaskConical, Loader2, ArrowRight, TrendingUp,
  MessageSquare, FileText, Database, Award, Image, ListOrdered, Type, BookOpen, HelpCircle
} from "lucide-react";

const getStrategyIcon = (id) => {
  switch (id) {
    case "addFAQ":
      return <MessageSquare className="w-5 h-5 text-primary" />;
    case "addSummary":
      return <FileText className="w-5 h-5 text-primary" />;
    case "addSchema":
      return <Database className="w-5 h-5 text-primary" />;
    case "improveAuthority":
      return <Award className="w-5 h-5 text-primary" />;
    case "optimizeMedia":
      return <Image className="w-5 h-5 text-primary" />;
    case "addLists":
      return <ListOrdered className="w-5 h-5 text-primary" />;
    case "semanticHeadings":
      return <Type className="w-5 h-5 text-primary" />;
    case "addReferences":
      return <BookOpen className="w-5 h-5 text-primary" />;
    default:
      return <HelpCircle className="w-5 h-5 text-primary" />;
  }
};

const scanMessages = [
  "Connecting to your page...",
  "Reading page content and structure...",
  "Checking how AI models would read this...",
  "Analyzing what makes content citable...",
  "Comparing against top-performing pages...",
  "Scoring visibility across AI engines...",
  "Testing optimization strategies...",
  "Ranking the best improvements for you..."
];

export default function SimulatorPage() {
  const [url, setUrl] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [error, setError] = useState("");
  const [scanStep, setScanStep] = useState(0);

  // Status rotator interval — stops at last step, never loops
  useEffect(() => {
    let interval;
    if (loading) {
      setScanStep(0);
      interval = setInterval(() => {
        setScanStep((prev) => {
          if (prev >= scanMessages.length - 1) {
            clearInterval(interval);
            return prev;
          }
          return prev + 1;
        });
      }, 1400);
    }
    return () => clearInterval(interval);
  }, [loading]);

  const handleSimulate = async (e) => {
    e.preventDefault();
    if (!url.trim() || !query.trim()) return;
    setError("");
    setLoading(true);
    setResult(null);
    try {
      const data = await analyzeUrlStrategies(url.trim(), query.trim());
      setResult(data);
      setSelectedIdx(0);
    } catch (err) {
      setError(err.response?.data?.detail || "Simulation analysis failed");
    }
    setLoading(false);
  };

  // Extract selected active strategy
  const activeStrategy = result?.recommendations?.[selectedIdx] || null;
  const bestDelta = result?.recommendations?.[0]?.improvement_delta || 0;

  return (
    <div className="space-y-8" data-testid="simulator-page">
      {/* Page Header */}
      <div>
        <h1 className="font-display text-3xl font-bold text-white mb-2">
          Strategy <span className="bg-gradient-to-r from-primary to-primary-hover bg-clip-text text-transparent">Simulator</span>
        </h1>
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>
          Submit a URL to analyze live signals and discover the best content strategies to boost your citation probability.
        </p>
      </div>

      {/* Main Input Form */}
      <div className="glass-card p-6">
        <form onSubmit={handleSimulate} className="space-y-4" data-testid="simulator-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider mb-2 block">
                Search Query
              </label>
              <input
                data-testid="sim-query-input"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="What target search query should we optimize for?"
                className="glass-input w-full h-12 px-4 text-sm"
                required
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 uppercase tracking-wider mb-2 block">
                Page URL
              </label>
              <input
                data-testid="sim-url-input"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/blog-post"
                className="glass-input w-full h-12 px-4 text-sm"
                required
              />
            </div>
          </div>

          <button
            data-testid="sim-submit"
            type="submit"
            disabled={loading}
            className="btn-primary w-full md:w-auto px-6 h-12 flex items-center justify-center gap-2 font-medium"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
            {loading ? "Analyzing Live Webpage..." : "Analyze & Recommend Strategies"}
          </button>
        </form>
      </div>

      {/* Error alert */}
      {error && (
        <div className="rounded-xl bg-red-400/10 border border-red-400/20 px-4 py-3 text-sm text-red-400">
          {error}
        </div>
      )}

      {/* Premium Strategy Simulation Console */}
      {loading && (
        <div className="glass-card p-8 md:p-10 flex flex-col items-center justify-center min-h-[400px] relative overflow-hidden" data-testid="sim-loading">
          {/* Ambient background glow */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(ellipse 50% 60% at 50% 40%, rgba(163,230,53,0.06) 0%, transparent 70%)',
          }} />

          {/* Orbital system */}
          <div className="relative w-32 h-32 flex items-center justify-center mb-8">
            {/* Orbit ring 1 — slow */}
            <div className="absolute inset-0 sim-orbit-ring-1">
              <div className="sim-orbit-dot" style={{ top: 0, left: '50%', transform: 'translateX(-50%)' }} />
            </div>
            {/* Orbit ring 2 — counter-rotate */}
            <div className="absolute sim-orbit-ring-2" style={{ inset: '8px' }}>
              <div className="sim-orbit-dot-alt" style={{ bottom: 0, left: '50%', transform: 'translateX(-50%)' }} />
            </div>
            {/* Orbit ring 3 — tilted */}
            <div className="absolute sim-orbit-ring-3" style={{ inset: '18px' }}>
              <div className="sim-orbit-dot" style={{ top: '50%', right: 0, transform: 'translateY(-50%)' }} />
            </div>

            {/* Center icon */}
            <div className="relative z-10 w-14 h-14 rounded-2xl flex items-center justify-center sim-center-glow" style={{
              background: 'linear-gradient(135deg, rgba(163,230,53,0.15) 0%, rgba(163,230,53,0.05) 100%)',
              border: '1px solid rgba(163,230,53,0.25)',
            }}>
              <FlaskConical className="w-7 h-7 text-primary sim-icon-breathe" />
            </div>
          </div>

          {/* Title */}
          <h3 className="text-white font-semibold text-sm tracking-[0.15em] uppercase mb-1" style={{ letterSpacing: '0.15em' }}>
            Strategy Simulation
          </h3>
          <p className="text-[11px] text-gray-600 mb-6 font-mono">Running live analysis pipeline</p>

          {/* Terminal-style console */}
          <div className="w-full max-w-lg rounded-xl overflow-hidden mb-6" style={{
            background: '#0a0a0c',
            border: '1px solid rgba(255,255,255,0.06)',
          }}>
            {/* Terminal chrome */}
            <div className="flex items-center gap-1.5 px-4 py-2.5" style={{ background: '#111113', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <div className="w-2 h-2 rounded-full" style={{ background: '#ff5f57' }} />
              <div className="w-2 h-2 rounded-full" style={{ background: '#febc2e' }} />
              <div className="w-2 h-2 rounded-full" style={{ background: '#28c840' }} />
              <span className="ml-3 text-[9px] font-mono text-gray-600">scopuz — strategy-simulator</span>
              <span className="ml-auto text-[9px] font-mono text-primary/50">● live</span>
            </div>
            {/* Terminal body */}
            <div className="px-5 py-4 space-y-2 h-[160px] overflow-y-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }} ref={el => { if (el) el.scrollTop = el.scrollHeight; }}>
              <style>{`.sim-terminal-body::-webkit-scrollbar { display: none; }`}</style>
              {scanMessages.slice(0, scanStep + 1).map((msg, i, arr) => (
                <div key={msg} className="flex items-center gap-2.5" style={{
                  opacity: i < arr.length - 2 ? 0.35 : i === arr.length - 2 ? 0.6 : 1,
                }}>
                  <span className="text-[10px] font-mono shrink-0" style={{ color: i === arr.length - 1 ? '#a3e635' : '#2a2a2a' }}>
                    {i === arr.length - 1 ? '▶' : '✓'}
                  </span>
                  <span className={`text-[12px] font-mono leading-relaxed ${i === arr.length - 1 ? 'text-gray-300' : 'text-gray-600'}`}>
                    {msg}
                    {i === arr.length - 1 && <span className="sim-terminal-cursor">▌</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Segmented progress */}
          <div className="flex items-center gap-1.5">
            {scanMessages.map((_, i) => (
              <div
                key={i}
                className="h-1 rounded-full transition-all duration-500"
                style={{
                  width: i <= scanStep ? '20px' : '8px',
                  background: i <= scanStep
                    ? 'var(--primary)'
                    : 'rgba(255,255,255,0.06)',
                  boxShadow: i === scanStep ? '0 0 8px rgba(163,230,53,0.4)' : 'none',
                }}
              />
            ))}
          </div>
          <p className="text-[10px] font-mono text-gray-600 mt-2">
            Step {scanStep + 1} of {scanMessages.length}
          </p>
        </div>
      )}

      {/* Dynamic results layout */}
      {result && (
        <div className="space-y-6" data-testid="sim-result">
          {/* Header overview and high-level potential metrics */}
          <div className="glass-card p-6 flex flex-col md:flex-row md:items-center justify-between gap-6 border-l-4 border-l-primary">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">AEO Analysis Summary</p>
              <h2 className="text-white font-semibold text-lg mt-1">Recommended Optimization Pathway</h2>
              <p className="text-sm text-gray-400 mt-1">
                Applying the single most impactful recommendation can increase your probability by <span className="text-primary font-semibold">+{bestDelta}%</span>, placing you in the <span className="text-white font-medium">{result.recommendations[0]?.simulated_position}</span> range.
              </p>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="text-center px-4 py-2 bg-white/[0.01] rounded-xl border border-white/5">
                <p className="text-2xl font-light text-gray-400">{result.original_probability}%</p>
                <p className="text-xs text-gray-500 mt-0.5">Baseline AEO</p>
              </div>
              <ArrowRight className="w-5 h-5 text-gray-600" />
              <div className="text-center px-4 py-2 bg-primary/5 rounded-xl border border-primary/10">
                <p className="text-2xl font-semibold text-primary">
                  {result.recommendations[0]?.simulated_probability}%
                </p>
                <p className="text-xs text-primary/70 mt-0.5">Top Potential</p>
              </div>
            </div>
          </div>

          {/* Grid of the 4 Recommended strategies */}
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Top 4 Optimization Recommendations</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {result.recommendations.map((rec, index) => {
                const isSelected = selectedIdx === index;
                return (
                  <button
                    key={rec.strategy}
                    type="button"
                    onClick={() => setSelectedIdx(index)}
                    data-testid={`strategy-${rec.strategy}`}
                    className={`text-left p-5 rounded-xl border transition-all duration-300 relative overflow-hidden recommendation-card-stagger ${
                      isSelected 
                        ? "active-recommendation-card" 
                        : "border-white/5 bg-white/[0.02] hover:border-white/10 hover:bg-white/[0.04]"
                    }`}
                  >
                    {/* Floating Delta Pill */}
                    <div className="absolute top-3 right-3 flex items-center gap-1 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                      <TrendingUp className="w-3 h-3 text-emerald-400" />
                      <span className="text-[10px] font-bold text-emerald-400">+{rec.improvement_delta}%</span>
                    </div>

                    {/* Icon wrapper */}
                    <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center mb-4">
                      {getStrategyIcon(rec.strategy)}
                    </div>

                    <p className="text-sm font-semibold text-white truncate">{rec.strategy_label}</p>
                    <p className="text-xs text-gray-400 mt-2 line-clamp-2 leading-relaxed">
                      {rec.strategy_description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Focused simulation metrics comparison panel */}
          {activeStrategy && (
            <div className="glass-card p-6 space-y-6 transition-all duration-300">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-white/5 pb-4 gap-4">
                <div>
                  <span className="text-[10px] text-primary uppercase font-bold tracking-wider px-2.5 py-1 bg-primary/10 rounded-full border border-primary/20">Simulation Live Preview</span>
                  <h4 className="text-white font-semibold text-lg mt-2">{activeStrategy.strategy_label}</h4>
                  <p className="text-sm text-gray-500">{activeStrategy.strategy_description}</p>
                </div>
                
                {/* Score improvement delta hero */}
                <div className="flex items-center gap-8 bg-white/[0.01] border border-white/5 px-6 py-3 rounded-2xl">
                  <div className="text-center">
                    <p className="text-2xl font-light" style={{ color: getScoreColor(activeStrategy.original_probability) }}>
                      {activeStrategy.original_probability}%
                    </p>
                    <p className="text-[10px] text-gray-500 mt-0.5">Original</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-600" />
                  <div className="text-center">
                    <p className="text-2xl font-semibold" style={{ color: getScoreColor(activeStrategy.simulated_probability) }}>
                      {activeStrategy.simulated_probability}%
                    </p>
                    <p className="text-[10px] text-gray-500 mt-0.5">Simulated</p>
                  </div>
                  <div className="h-8 w-px bg-white/10"></div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-emerald-400">+{activeStrategy.improvement_delta}%</p>
                    <p className="text-[10px] text-emerald-400/80 mt-0.5">Delta Improvement</p>
                  </div>
                </div>
              </div>

              {/* Scoring breakdown table */}
              <div className="space-y-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider">AEO Dimension Impact</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {Object.keys(activeStrategy.original_breakdown || {}).map((key) => {
                    const orig = activeStrategy.original_breakdown[key];
                    const sim = activeStrategy.simulated_breakdown[key];
                    const delta = sim - orig;
                    return (
                      <div key={key} className="rounded-xl bg-white/[0.01] border border-white/5 p-3 text-center transition-all duration-300">
                        <p className="text-[10px] text-gray-500 mb-2 capitalize">{key.replace(/_/g, " ")}</p>
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-xs" style={{ color: getScoreColor(orig) }}>{orig}</span>
                          <ArrowRight className="w-2.5 h-2.5 text-gray-600" />
                          <span className="text-xs font-semibold" style={{ color: getScoreColor(sim) }}>{sim}</span>
                        </div>
                        {delta !== 0 ? (
                          <p className={`text-[10px] mt-1 font-bold ${delta > 0 ? "text-emerald-400" : "text-red-400"}`}>
                            {delta > 0 ? "+" : ""}{delta}
                          </p>
                        ) : (
                          <p className="text-[10px] mt-1 text-gray-600">No Change</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Actionable adjustments applied by optimization */}
              {activeStrategy.adjustments_applied?.length > 0 && (
                <div className="bg-white/[0.01] border border-white/5 rounded-xl p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Simulated Adjustments Applied</p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {activeStrategy.adjustments_applied.map((a, i) => (
                      <li key={i} className="text-xs text-gray-400 flex items-center gap-2">
                        <ArrowRight className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Explanatory description */}
              <p className="text-xs text-gray-400 leading-relaxed border-t border-white/5 pt-4">
                {activeStrategy.explanation}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
